import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { toast } from 'react-toastify';
import * as api from './api';
import type { ApproveStagingPayload, RejectStagingPayload } from './api';
import type { ListStagingParams } from './schema';

const ROOT_KEY = 'observationStaging';
const listKey = (params: ListStagingParams) =>
  [ROOT_KEY, 'list', params] as const;

function describeError(err: unknown, fallback: string): string {
  if (isAxiosError(err)) {
    const data = err.response?.data as
      | { message?: string | string[]; error?: string }
      | undefined;
    const msg = data?.message;
    if (Array.isArray(msg)) return msg.join(', ');
    if (typeof msg === 'string' && msg.length > 0) return msg;
    if (data?.error) return data.error;
  }
  return fallback;
}

export function useObservationStaging(params: ListStagingParams) {
  return useQuery({
    queryKey: listKey(params),
    queryFn: () => api.listObservationStaging(params),
    placeholderData: (prev) => prev,
    // Presigned photo URLs live 5 min; keep list data fresh so thumbnails don't
    // 403 after sitting open. Refetch on focus and cap staleness at ~4 min.
    staleTime: 4 * 60 * 1000,
  });
}

// Single-row detail. Unlike the list, this carries the *verbatim* WhatsApp
// message (the server reads it from S3 only on this path), so the review dialog
// fetches the row by id when it opens rather than making the list pay for every
// row's S3 read. `enabled` gates it to an open dialog.
export function useObservationStagingRecord(id: string | null, enabled: boolean) {
  return useQuery({
    queryKey: [ROOT_KEY, 'record', id] as const,
    queryFn: () => api.getObservationStaging(id as string),
    enabled: enabled && !!id,
    staleTime: 4 * 60 * 1000,
  });
}

export function useApproveObservation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: ApproveStagingPayload }) =>
      api.approveObservation(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ROOT_KEY, 'list'] });
      toast.success('Observation approved & published');
    },
    onError: (err) =>
      toast.error(describeError(err, 'Failed to approve observation')),
  });
}

export function useRejectObservation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: RejectStagingPayload }) =>
      api.rejectObservation(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ROOT_KEY, 'list'] });
      toast.success('Observation rejected');
    },
    onError: (err) =>
      toast.error(describeError(err, 'Failed to reject observation')),
  });
}
